/**
 * Lightweight error reporter — bridge towards Sentry.
 * Sends client errors to the `log-client-error` edge function which writes to `error_logs`.
 *
 * Design goals:
 * - Never throw, never block UI, never log secrets.
 * - Deduplicate identical errors within a short window.
 * - Honor user consent (skipped when analytics consent not granted).
 * - When Sentry is added later, swap the transport here without changing call sites.
 */

import { consentAllowsAnalytics } from "@/lib/consent";
import { getSupabaseFunctionUrl, getSupabaseRuntimeConfig } from "@/lib/supabaseRuntimeConfig";

const RELEASE = (import.meta.env.MODE ?? "development") + "@" + (import.meta.env.VITE_APP_VERSION ?? "dev");

const recent = new Map<string, number>();
const DEDUP_WINDOW_MS = 30_000;
const MAX_QUEUE = 20;
let sent = 0;

type Level = "error" | "warn" | "info";

interface ReportOptions {
  level?: Level;
  context?: Record<string, unknown>;
  /**
   * Set to true for operational errors that block the user's flow (failed
   * uploads, failed submissions, broken auth handoff, etc.). These are NOT
   * analytics — they are app errors the team needs to see even when the
   * user has refused analytics consent. The payload still excludes IP and
   * user_agent at write time on the server side; only the error message,
   * stack and context get persisted to error_logs.
   */
  forceReport?: boolean;
}

// AbortError appears whenever the browser cancels in-flight fetch/XHR calls
// during navigation, refresh, or bfcache restore. Treat it as benign noise
// (same policy as the boot handler in index.html).
//
// Exported so flow-critical call sites (e.g. PostAuthProcess) can distinguish
// a cancelled request — typical on iOS Safari when the originating tab is
// backgrounded while the user opens the confirmation email — from a real
// backend failure, and retry instead of surfacing "Errore di invio".
export function isBenignAbortError(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const r = value as { name?: string; code?: number; message?: string };
  if (r.name === "AbortError" || r.code === 20) return true;
  const msg = typeof r.message === "string" ? r.message : "";
  if (
    msg === "The operation was aborted." ||
    msg === "The user aborted a request." ||
    msg.includes("signal is aborted") ||
    (msg.includes("aborted") && msg.includes("fetch"))
  ) return true;
  return false;
}

function fingerprint(message: string, stack?: string): string {
  const head = (stack ?? "").split("\n").slice(0, 3).join("|");
  return `${message}::${head}`.slice(0, 300);
}

export function reportError(error: unknown, opts: ReportOptions = {}): void {
  if (sent >= MAX_QUEUE) return;
  if (typeof window === "undefined") return;
  // Critical app-flow errors bypass the analytics gate — they document the
  // user's broken experience right now, not analytics-style behaviour data.
  // Without this exception every "Errore di invio" on a user who refused
  // analytics consent disappears into thin air on the server side.
  if (!opts.forceReport && !consentAllowsAnalytics()) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const fp = fingerprint(err.message, err.stack);
  const now = Date.now();
  const last = recent.get(fp);
  if (last && now - last < DEDUP_WINDOW_MS) return;
  recent.set(fp, now);

  // Primary transport: Sentry (only initialized in production, gated by consent).
  // Best-effort dynamic import so dev/preview builds don't pay for it.
  if (import.meta.env.MODE === "production") {
    import("@sentry/react").then((Sentry) => {
      // Map our Level → Sentry SeverityLevel ("warn" → "warning")
      const sentryLevel = opts.level === "warn" ? "warning" : (opts.level ?? "error");
      Sentry.captureException(err, {
        level: sentryLevel as "error" | "warning" | "info",
        contexts: { app: { ...(opts.context ?? {}) } },
      });
    }).catch(() => { /* swallow */ });
  }

  const payload = {
    message: err.message,
    stack: err.stack,
    url: window.location.href,
    user_agent: navigator.userAgent,
    level: opts.level ?? "error",
    context: opts.context ?? {},
    release: RELEASE,
  };

  sent++;
  // Backup transport: write to error_logs DB (works even if Sentry is down).
  // Fire-and-forget, never blocks UI. Uses the same runtime client config as
  // the database client, avoiding stale build env after backend migrations.
  try {
    const { supabaseKey } = getSupabaseRuntimeConfig();
    fetch(getSupabaseFunctionUrl("log-client-error"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: supabaseKey,
        Authorization: `Bearer ${supabaseKey}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Swallow: we never want logging to throw
    });
  } catch {}
}

let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error ?? new Error(event.message);
    if (isBenignAbortError(err)) return;
    reportError(err, {
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason ?? new Error("Unhandled rejection");
    if (isBenignAbortError(reason)) return;
    reportError(reason, {
      context: { type: "unhandledrejection" },
    });
  });
}
